import { Card } from '@/components/UI/Container/Card'
import { useOrderBookStatusOpen } from '@/hooks/useOrderBookStatusOpen'
import { useCustomers } from '@/hooks/useCustomers'
import { Button, Popconfirm, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import _ from 'lodash'
import { FC, useCallback, useMemo } from 'react'
import { MonitorIcon } from 'lucide-react'
import {
  updateOrderComplete,
  switchOrderMonitorDisplay,
  assignOrderToMonitor,
} from '@/supabase/Orderbook/orderbook'
import { useAppDispatch } from '@/state/hooks'
import {
  setOrderFormStatus,
  setOrderIdForEdit,
} from '@/state/orderManager/reducer'
import { CheckCircleTwoTone, ToolFilled } from '@ant-design/icons'

export const RightSection: FC = () => {
  const dispatch = useAppDispatch()
  const orderbooksStatusOpen = useOrderBookStatusOpen()
  const customers = useCustomers()

  const { orderQueue, orderMonitorOne, orderMonitorTwo } = useMemo(() => {
    const orderSort = _.orderBy(orderbooksStatusOpen, 'created_at', 'asc')
    return {
      orderQueue: _.filter(orderSort, { monitor_number: 0 }),
      orderMonitorOne: _.filter(orderSort, { monitor_number: 1 }),
      orderMonitorTwo: _.filter(orderSort, { monitor_number: 2 }),
    }
  }, [orderbooksStatusOpen])

  const handleClickActiveOrder = useCallback(
    async (orderId: number, monitorNo: number, isMonitorDisplay: boolean) => {
      await switchOrderMonitorDisplay(orderId, monitorNo, isMonitorDisplay)
    },
    []
  )

  const handleClickConfirmOrder = useCallback(async (orderId: number) => {
    await updateOrderComplete(orderId)
  }, [])

  const handleClickOrderForUpdate = useCallback(
    (orderId: number) => {
      dispatch(setOrderFormStatus('UPDATE'))
      dispatch(setOrderIdForEdit(orderId))
    },
    [dispatch]
  )

  const handleAssignToMonitor = useCallback(
    async (orderId: number, monitorNumber: number) => {
      await assignOrderToMonitor(orderId, monitorNumber)
    },
    []
  )

  // Helper function to get customer name by ID
  const getCustomerName = useCallback((customerId: string) => {
    const customer = customers?.find(c => c.id === customerId)
    return customer?.name || '-'
  }, [customers])

  const queueColumns: ColumnsType<any> = [
    {
      title: 'Monitor',
      dataIndex: 'id',
      key: 'monitor_assignment',
      align: 'center',
      width: 120,
      render: (id: number) => (
        <div className='flex gap-3 justify-center'>
          <Button
            size='middle'
            onClick={() => handleAssignToMonitor(id, 1)}
            className='flex items-center gap-2 min-w-[50px] h-10 font-semibold'
            icon={<MonitorIcon size={14} />}
          >
            1
          </Button>
          <Button
            size='middle'
            onClick={() => handleAssignToMonitor(id, 2)}
            className='flex items-center gap-2 min-w-[50px] h-10 font-semibold'
            icon={<MonitorIcon size={14} />}
          >
            2
          </Button>
        </div>
      ),
    },
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      align: 'center',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_id',
      key: 'customer_name',
      align: 'center',
      width: 120,
      render: (customerId: string) => getCustomerName(customerId),
    },
    {
      title: 'ใหญ่',
      dataIndex: 'large_ice_cube_amount',
      key: 'large_ice_cube_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'เล็ก',
      dataIndex: 'small_ice_cube_amount',
      key: 'small_ice_cube_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'โม่',
      dataIndex: 'crushed_ice_amount',
      key: 'crushed_ice_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'มย',
      dataIndex: 'coarsely_crushed_ice_amount',
      key: 'coarsely_crushed_ice_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'Confirm',
      key: 'confirm_placeholder',
      align: 'center',
      width: 100,
      render: () => (
        <span className='text-gray-300'>-</span>
      ),
    },
    {
      title: 'Edit',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 80,
      render: (id: number) => (
        <Button
          icon={<ToolFilled />}
          onClick={() => handleClickOrderForUpdate(id)}
          size='small'
        >
          Edit
        </Button>
      ),
    },
  ]

  const columns: ColumnsType<any> = [
    {
      title: 'Monitor',
      dataIndex: 'is_monitor_display',
      key: 'is_monitor_display',
      align: 'center',
      width: 120,
      render: (isMonitorDisplay: boolean, record: any) => (
        <MonitorIcon
          size={30}
          color={
            isMonitorDisplay
              ? 'var(--color-monitor-screen-active-border)'
              : 'var(--color-monitor-screen-inactive-border)'
          }
          fill={
            isMonitorDisplay
              ? 'var(--color-monitor-screen-active-bg)'
              : 'var(--color-monitor-screen-inactive-bg)'
          }
          onClick={() =>
            handleClickActiveOrder(
              record.id,
              record.monitor_number,
              isMonitorDisplay
            )
          }
          className='m-auto cursor-pointer'
        />
      ),
    },
    {
      title: 'Order ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
      align: 'center',
    },
    {
      title: 'Customer',
      dataIndex: 'customer_id',
      key: 'customer_name',
      align: 'center',
      width: 120,
      render: (customerId: string) => getCustomerName(customerId),
    },
    {
      title: 'ใหญ่',
      dataIndex: 'large_ice_cube_amount',
      key: 'large_ice_cube_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'เล็ก',
      dataIndex: 'small_ice_cube_amount',
      key: 'small_ice_cube_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'โม่',
      dataIndex: 'crushed_ice_amount',
      key: 'crushed_ice_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'มย',
      dataIndex: 'coarsely_crushed_ice_amount',
      key: 'coarsely_crushed_ice_amount',
      width: 70,
      align: 'center',
      render: amount => (_.gt(amount, 0) ? amount : '-'),
    },
    {
      title: 'Confirm',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 100,
      render: (id: number) => (
        <Popconfirm
          title='Confirm order'
          description='Are you sure to confirm order?'
          okText='Yes'
          cancelText='No'
          placement='top'
          getPopupContainer={() => document.body}
          rootClassName='popconfirm-high-z-index'
          okButtonProps={{
            style: { fontSize: '14px' },
          }}
          cancelButtonProps={{
            style: { fontSize: '14px' },
          }}
          onConfirm={() => handleClickConfirmOrder(id)}
        >
          <Button
            icon={<CheckCircleTwoTone twoToneColor='#52c41a' />}
            size='small'
          >
            Confirm
          </Button>
        </Popconfirm>
      ),
    },
    {
      title: 'Edit',
      dataIndex: 'id',
      key: 'id',
      align: 'center',
      width: 80,
      render: (id: number) => (
        <Button
          icon={<ToolFilled />}
          onClick={() => handleClickOrderForUpdate(id)}
          size='small'
        >
          Edit
        </Button>
      ),
    },
  ]

  return (
    <div className='ml-5 w-full h-full flex flex-col gap-3 overflow-hidden'>
      {/* Queue Card - Fixed height */}
      <Card className='w-full p-3 flex-shrink-0 flex flex-col' style={{ height: '30%' }}>
        <div className='mb-2 font-semibold text-base text-gray-800 flex-shrink-0'>Queue</div>
        <div className='flex-1 overflow-y-auto min-h-0'>
          <Table
            columns={queueColumns}
            rowKey='id'
            dataSource={orderQueue}
            pagination={false}
            size='small'
            className='w-full [&_.ant-table]:w-full [&_table]:w-full'
            locale={{
              emptyText: 'No orders in queue',
            }}
          />
        </div>
      </Card>

      {/* Monitor Cards - Vertical split (top-bottom) */}
      <div className='flex-1 flex flex-col gap-3 min-h-0'>
        <Card className='flex-1 p-3 flex flex-col min-h-0'>
          <div className='mb-2 font-semibold text-base text-gray-800 flex-shrink-0'>
            Monitor 1
          </div>
          <div className='flex-1 overflow-y-auto min-h-0'>
            <Table
              columns={columns}
              rowKey='id'
              dataSource={orderMonitorOne}
              pagination={false}
              size='small'
              className='w-full [&_.ant-table]:w-full [&_table]:w-full'
              locale={{
                emptyText: 'No orders on Monitor 1',
              }}
            />
          </div>
        </Card>
        <Card className='flex-1 p-3 flex flex-col min-h-0'>
          <div className='mb-2 font-semibold text-base text-gray-800 flex-shrink-0'>
            Monitor 2
          </div>
          <div className='flex-1 overflow-y-auto min-h-0'>
            <Table
              columns={columns}
              rowKey='id'
              dataSource={orderMonitorTwo}
              pagination={false}
              size='small'
              className='w-full [&_.ant-table]:w-full [&_table]:w-full'
              locale={{
                emptyText: 'No orders on Monitor 2',
              }}
            />
          </div>
        </Card>
      </div>
    </div>
  )
}
